// SAM-1304: a pass of an event, given by the tenant admin or the tenant Front Desk in New pass.
// It mirrors businessAccess `_fd_event_pass_error` / `check_event_capacity` so the list never offers a dead end;
// the backend still decides and answers 404 / 409 / 403 (EVENT_NOT_OPEN, EVENT_PRIVATE, EVENT_FINISHED, EVENT_FULL).
import { isPrivateEvent } from '../../utils/privateEvent';
import { eventStatusOf, type Event } from '../../hooks/useEvents';

/** What the pass keeps of the chosen event: its id, the business the pass belongs to (the event's) and a name. */
export interface EventPassChoice {
  id: string;
  business_id: string;
  name: string;
}

/**
 * Only the tenant admin and the tenant Front Desk give event passes. The estate group roles stay out of this ticket:
 * the server runs `_fd_event_pass_error` for them too and answers 403 when the event's business is outside their
 * businesses, so opening the chip to them is a separate ticket (backend + Security).
 */
export const mayGiveEventPass = (p: { isAdmin: boolean; isTenantFrontDesk: boolean }): boolean =>
  p.isAdmin || p.isTenantFrontDesk;

const SANTO_DOMINGO_TZ = 'America/Santo_Domingo';

/** "YYYY-MM-DDTHH:mm:ss" on the wall clock of Santo Domingo, the one event.end_date is compared with. */
export const santoDomingoNow = (now: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: SANTO_DOMINGO_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).format(now);
  return parts.replace(' ', 'T');
};

/** The event's end_date is a plain Santo Domingo date-time: compare its first 19 characters, whatever suffix it has. */
export const eventHasEnded = (event: Pick<Event, 'end_date'>, now: Date = new Date()): boolean => {
  const end = (event.end_date ?? '').slice(0, 19);
  return end.length === 19 && end < santoDomingoNow(now);
};

/** Published, active, public, not over and with a business of its own: the events a pass can be given for. */
export const isOfferableEvent = (event: Event, now: Date = new Date()): boolean =>
  Boolean(event.id) &&
  Boolean(event.business_id) &&
  eventStatusOf(event) === 'published' &&
  event.active !== false &&
  !isPrivateEvent(event) &&
  !eventHasEnded(event, now);

export const filterOfferableEvents = (events: Event[] | undefined, now: Date = new Date()): Event[] =>
  (events ?? [])
    .filter((e) => isOfferableEvent(e, now))
    .sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''));

export const eventDisplayName = (event: Event, lang: 'en' | 'es'): string =>
  (lang === 'es' ? event.name_es || event.name || event.name_en : event.name_en || event.name || event.name_es) ||
  event.id;

export const toEventPassChoice = (event: Event, lang: 'en' | 'es'): EventPassChoice => ({
  id: event.id,
  business_id: String(event.business_id),
  name: eventDisplayName(event, lang),
});
